import { useState } from "react";
import { Upload, X } from "lucide-react";
import { api, asset, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CHUNK = 20 * 1024 * 1024;

async function uploadChunked(file, onProgress) {
  const { data } = await api.post("/upload/chunked/start", { filename: file.name, size: file.size });
  const total = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), `${file.name}.part${i}`);
    await api.post(`/upload/chunked/${data.upload_id}/part/${i}`, fd, {
      onUploadProgress: (p) => onProgress(Math.min(99, Math.round(((i * CHUNK + p.loaded) / file.size) * 100))),
    });
  }
  onProgress(100);
  const res = await api.post(`/upload/chunked/${data.upload_id}/finish`, { total });
  return res.data;
}

export async function uploadFile(file, onProgress) {
  if (file.size > 40 * 1024 * 1024) return uploadChunked(file, onProgress);
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/upload", fd, {
    onUploadProgress: (p) => onProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
  });
  return res.data;
}

export const FileField = ({ label, value, onChange, accept, kind, testid }) => {
  const [progress, setProgress] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setProgress(0);
    try {
      const data = await uploadFile(file, setProgress);
      onChange(data.url);
      toast.success(`${file.name} UPLOADED`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div>
      <p className="font-mono text-[11px] tracking-[0.25em] text-zinc-400 mb-2">{label}</p>
      <div className="flex gap-3 items-stretch">
        {kind === "image" && value && <img src={asset(value)} alt="" className="w-16 h-16 object-cover border border-[#F5A524]/30" />}
        {kind === "video" && value && !/youtu/.test(value) && <video src={`${asset(value)}#t=0.5`} muted preload="metadata" className="w-24 h-16 object-cover border border-[#F5A524]/30 bg-black" />}
        <div className="flex-1 space-y-2">
          <input data-testid={`${testid}-url-input`} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Upload a file or paste a URL"
            className="w-full bg-[#0B0C10] border border-[#F5A524]/30 focus:border-[#F5A524] outline-none px-3 py-2 font-mono text-xs text-white" />
          <div className="flex gap-2">
            <label data-testid={`${testid}-upload-btn`} className="cursor-pointer flex items-center gap-2 px-3 py-2 bg-[#F5A524] text-black font-mono text-[11px] font-bold tracking-[0.2em] hover:bg-white transition-colors">
              <Upload size={13} /> {progress === null ? "UPLOAD" : progress >= 100 ? "PROCESSING..." : `UPLOADING ${progress}%`}
              <input type="file" accept={accept} className="hidden" onChange={onFile} disabled={progress !== null} />
            </label>
            {value && (
              <button type="button" data-testid={`${testid}-clear-btn`} onClick={() => onChange("")}
                className="flex items-center gap-1 px-3 py-2 border border-[#FF2A55]/50 text-[#FF2A55] font-mono text-[11px] tracking-[0.2em] hover:bg-[#FF2A55] hover:text-black transition-colors">
                <X size={13} /> CLEAR
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
